import React, { PureComponent, Fragment } from "react";
import { connect } from "react-redux";
import { PropTypes } from "prop-types";
import {
  Container,
  Table,
  Header,
  Pagination,
  Divider,
  Loader
} from "semantic-ui-react";

import UnauthorisedFeature from "../../components/auth/UnauthorisedFeature.jsx";
import InvoiceFilters from "../../components/invoices/InvoiceFilters.jsx";
import InvoiceRow from "../../components/invoices/InvoiceRow.jsx";
import RedirectWithReset from "../../components/utils/RedirectWithReset.jsx";

import { OPERATIONS } from "../../constants/operations.js";
import { AT_TYPES } from "../../constants/entity.js";

import {
  getURLFilterParams,
  updateFilterSearchParams,
  updatePageSearchParams
} from "../../utils/utils.js";

import { getInvoices } from "../../state/reducers/invoices/invoiceActions.js";
import endpointSelector from "../../state/reducers/endpoints/endpointSelector.js";
import { selectCopy } from "../../state/reducers/locale/copySelector.js";

class Invoices extends PureComponent {
  static propTypes = {
    copyKey: PropTypes.string.isRequired,

    dataIndex: PropTypes.array,
    dataMap: PropTypes.object,
    totalPages: PropTypes.number,
    page: PropTypes.number,
    isFetching: PropTypes.bool.isRequired,
    isErrored: PropTypes.bool,
    listInvoiceAvailability: PropTypes.object.isRequired,
    copy: PropTypes.func.isRequired,

    getInvoices: PropTypes.func.isRequired
  };

  static defaultProps = {
    copyKey: "invoices"
  };

  constructor(props) {
    super(props);

    this.state = {
      filters: null,
      redirect: null
    };
  }

  // -- PAGE DATA MANAGEMENT -- //

  static getDerivedStateFromProps(props, state) {
    const { location } = props;

    const redirect = state.redirect === location.search ? null : state.redirect;

    return {
      ...state,
      filters: getURLFilterParams(location),
      redirect
    };
  }

  componentDidMount() {
    this.getData();
  }

  componentDidUpdate(prevProps) {
    // do search params change
    if (this.props.location.search !== prevProps.location.search) {
      this.getData();
    }
  }

  getData = () => {
    const {
      location,
      isFetching,
      isErrored,
      getInvoices,
      listInvoiceAvailability: { available }
    } = this.props;

    if (!isFetching && !isErrored && available) {
      getInvoices(location.search);
    }
  };

  // -- HANDLERS & ACTIONS -- //

  onFilterChange = data => {
    const redirect = updateFilterSearchParams(
      this.props.location.search,
      data.filters
    ).toString();

    this.setState({
      filters: data.filters,
      redirect: `?${redirect}`
    });
  };

  onPageChange = (evt, { activePage }) => {
    const redirect = updatePageSearchParams(
      this.props.location.search,
      activePage
    ).toString();

    this.setState({
      redirect: `?${redirect}`
    });
  };

  // -- RENDER -- //

  renderInvoiceRows() {
    const { dataMap, dataIndex } = this.props;

    if (!dataMap) return null;

    return dataIndex.map(index => {
      const invoice = dataMap[index];

      return <InvoiceRow key={invoice["@id"]} data={invoice} />;
    });
  }

  render() {
    const { filters, redirect } = this.state;
    const {
      page,
      totalPages,
      isFetching,
      dataIndex,
      dataMap,
      location,
      listInvoiceAvailability: { permitted, enabled },
      copyKey,
      copy
    } = this.props;

    if (!permitted) {
      return <UnauthorisedFeature title={copy(`${copyKey}.title`)} />;
    }

    if (redirect) {
      return (
        <RedirectWithReset to={`${location.pathname}${redirect}`} push={true} />
      );
    }

    if (isFetching) {
      return (
        <Container>
          <InvoiceFilters
            onFilterChange={this.onFilterChange}
            initialValues={{ filters }}
          />

          <Header as="h3">{copy(`${copyKey}.title`)}</Header>

          <Loader active inline="centered" />
        </Container>
      );
    }

    return (
      <Container>
        <InvoiceFilters
          onFilterChange={this.onFilterChange}
          initialValues={{ filters }}
        />

        <Header as="h3">{copy(`${copyKey}.title`)}</Header>

        {(!enabled || !dataMap || !dataIndex || dataIndex.length < 1) && (
          <p>{copy(`${copyKey}.empty`)}</p>
        )}

        {dataIndex.length > 0 && (
          <Fragment>
            <Table celled selectable unstackable>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>{copy(`${copyKey}.id`)}</Table.HeaderCell>
                  <Table.HeaderCell>
                    {copy(`${copyKey}.week`)}*
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    {copy(`${copyKey}.from`)}*
                  </Table.HeaderCell>
                  <Table.HeaderCell>{copy(`${copyKey}.to`)}*</Table.HeaderCell>
                  <Table.HeaderCell>
                    {copy(`${copyKey}.total`)}
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    {copy(`${copyKey}.marking`)}
                  </Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>{this.renderInvoiceRows()}</Table.Body>
            </Table>
            <p className="mini-text float-right">
              *{copy(`${copyKey}.date_details`)}
            </p>
          </Fragment>
        )}

        {totalPages && totalPages > 1 && (
          <Fragment>
            <Pagination
              defaultActivePage={page}
              totalPages={totalPages}
              firstItem={null}
              lastItem={null}
              onPageChange={this.onPageChange}
              className={"clear-both float-right"}
            />
            <Divider hidden fitted clearing />
          </Fragment>
        )}
      </Container>
    );
  }
}

const mapStateToProps = state => ({
  page: state.invoices.page,
  totalPages: state.invoices.totalPages,
  isFetching: state.invoices.isFetching,
  isErrored: state.api.isError,
  dataIndex: state.invoices.dataIndex,
  dataMap: state.invoices.dataMap,
  listInvoiceAvailability: endpointSelector.selectOperationAvailability(state)(
    AT_TYPES.INVOICE,
    OPERATIONS.LIST
  ),
  copy: selectCopy(state)
});

const mapDispatchToProps = dispatch => ({
  getInvoices: page => dispatch(getInvoices(page))
});

export default connect(mapStateToProps, mapDispatchToProps)(Invoices);
